// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React from 'react';
import { Allocation } from '@daml.js/splice-api-token-allocation/lib/Splice/Api/Token/AllocationV1';
import { ChoiceContext } from '@daml.js/splice-api-token-metadata/lib/Splice/Api/Token/MetadataV1';
import { InstructedCollateral } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { UserService } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { ContractId } from '@daml/types';
import { Status } from '../../../components/StateCell';
import { useModuleParty } from '../../../contexts/ModuleContext';
import { useAllocationTransferContext } from '../../../hooks/mutations/allocation/useAllocationTransferContext';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import useCollateralState from '../../../hooks/queries/collateral/collateral/useCollateralState';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import useAllocations from '../../../hooks/queries/registry/tokenStandard/useAllocations';
import InterfaceContract from '../../../utils/InterfaceContract';
import { toTokenStandardDisclosedContract } from '../../../utils/common';
import { ERR_CAN_INST, ERR_SETTLE_COL, SUC_CAN_INST, SUC_SETTLE_COL } from '../../../utils/strings';
import { GenericAction, InstructedRow } from './types';

type Action = GenericAction<InstructedRow>;

const rejectCollateralOffer = (r: InstructedRow, reason: string) => {
  console.log(r, reason);
  return new Promise(() => {});
};

export const useCreateCollateralsActions = () => {
  const [selectedCid, setCid] = React.useState('');
  const [dialogName, setDialogName] = React.useState('');
  const party = useModuleParty();
  const onClose = () => {
    setDialogName('');
    setCid('');
  };
  const atc = useAllocationTransferContext();
  const allocations = useAllocations();
  const userService = useCollateralUserService();
  const userServiceCid = userService.data?.contractId;
  const collateralState = useCollateralState();

  const { submit: settleInstruction } = useExerciseSubmission(
    UserService.UserService_ExecuteTransfer,
    SUC_SETTLE_COL,
    ERR_SETTLE_COL,
  );

  const { submit: cancelInstruction } = useExerciseSubmission(
    UserService.UserService_CancelInstructedCollateral,
    SUC_CAN_INST,
    ERR_CAN_INST,
  );

  const getAllocationTransferContext = async (
    allocation: InterfaceContract<Allocation>,
    r: InstructedRow,
  ) => {
    const view = allocation.view(Allocation);
    const registrar =
      r.payload.positions.find(
        (i) => i.instrument.id === view.allocation.transferLeg.instrumentId.id,
      )?.instrument.admin || '';
    const allocationCid = allocation.contractId;
    const a = await atc.mutateAsync({
      registrar,
      allocationCid,
    });
    return a;
  };

  const onCancel = async (r: InstructedRow): Promise<void> => {
    if (!allocations.data) {
      throw Error('No allocation data');
    }

    const relevantAllocations = allocations.data
      .filter(
        (a) =>
          a.view(Allocation).allocation.settlement.settlementRef.id ===
          `${r.payload.agreementId};${r.payload.id}`,
      )
      .sort((a, b) =>
        a
          .view(Allocation)
          .allocation.transferLegId.localeCompare(b.view(Allocation).allocation.transferLegId),
      );
    const allocationCids = relevantAllocations?.map(
      (a) => a.contractId as unknown as ContractId<Allocation>,
    );
    const extraArgs = await Promise.all(
      relevantAllocations?.map((a) => getAllocationTransferContext(a, r)),
    );

    if (!userServiceCid) {
      throw Error('No UserService');
    }
    console.log('CA');
    const disclosedContracts = [
      ...new Map(
        (
          extraArgs.flatMap((a) =>
            a.disclosedContracts.map((x) => toTokenStandardDisclosedContract(x)),
          ) || []
        ).map((contract) => [contract.contractId, contract]),
      ).values(),
    ];

    await cancelInstruction({
      cid: userServiceCid,
      meta: {
        disclosedContracts,
      },
      argument: {
        cid: r.contractId as ContractId<InstructedCollateral>,
        allocations: allocationCids,
        extraArgs: extraArgs.map((c) => ({
          context: c.choiceContextData as ChoiceContext,
          meta: { values: {} },
        })),
      },
    });
  };

  const onSettle = async (r: InstructedRow): Promise<void> => {
    if (!allocations.data) {
      throw Error('No allocations');
    }

    const relevantAllocations = allocations.data
      .filter(
        (a) =>
          a.view(Allocation).allocation.settlement.settlementRef.id ===
          `${r.payload.agreementId};${r.payload.id}`,
      )
      .sort((a, b) =>
        a
          .view(Allocation)
          .allocation.transferLegId.localeCompare(b.view(Allocation).allocation.transferLegId),
      );

    const allocationCids = relevantAllocations?.map(
      (a) => a.contractId as unknown as ContractId<Allocation>,
    );
    const extraArgs = await Promise.all(
      relevantAllocations?.map((a) => getAllocationTransferContext(a, r)),
    );
    const collateralStateId =
      collateralState.data?.filter((c) => c.payload.id === r.payload.agreementId) || [];
    const [first] = collateralStateId;

    if (!allocationCids?.length) {
      throw Error('No allocations');
    }
    if (!userServiceCid) {
      throw Error('No UserService');
    }
    const disclosedContracts = [
      ...new Map(
        (
          extraArgs.flatMap((a) =>
            a.disclosedContracts.map((x) => toTokenStandardDisclosedContract(x)),
          ) || []
        ).map((contract) => [contract.contractId, contract]),
      ).values(),
    ];
    await settleInstruction({
      cid: userServiceCid,
      meta: {
        disclosedContracts,
      },
      argument: {
        cid: r.contractId as ContractId<InstructedCollateral>,
        allocations: allocationCids,
        executeTransferArgs: extraArgs.map((c) => ({
          context: c.choiceContextData as ChoiceContext,
          meta: { values: {} },
        })),
        collateralStateCid: first.contractId,
      },
    });
    onClose();
  };

  const [onReject, setOnReject] = React.useState<
    ((reason: string) => Promise<unknown>) | undefined
  >();
  const createCollateralsActions = (r: InstructedRow) => {
    let actions: Action[] = [];
    // TODO: Before merging check whether it is still valid. `Requested` is not listed in Status union.
    if (r.state === ('Requested' as Status)) {
      actions = actions.concat([
        {
          name: 'Accept',
          action: () => {
            return new Promise(() => {});
          },
        },
        {
          name: 'Reject',
          buttonProps: {
            variant: 'outlined',
          },
          action: () => {
            setOnReject(() => (reason: string) => rejectCollateralOffer(r, reason));
            return Promise.resolve();
          },
        },
      ]);
    }
    if (r.state === 'Accepted') {
      actions = actions.concat([
        {
          name: 'Instruct',
          buttonProps: {
            variant: 'outlined',
          },
          action: () => {
            return Promise.resolve();
          },
        },
      ]);
    }
    if (r.state === 'Settled') {
      actions = actions.concat([
        {
          name: 'View details',
          buttonProps: {
            variant: 'outlined',
          },
          action: () => {
            setDialogName('details');
            return Promise.resolve();
          },
        },
      ]);
    }
    if (r.state === 'Instructed') {
      actions = actions.concat([
        {
          name: 'View details',
          buttonProps: {
            variant: 'outlined',
          },
          action: () => {
            setDialogName('instruct');
            return Promise.resolve();
          },
        },
      ]);
      actions = actions.concat({
        name: 'Cancel instruction',
        action: () => {
          setDialogName('cancelInstruct');
          return Promise.resolve();
        },
      });
      if (party === r.payload.positions[0].pledgor) {
        actions = actions.concat([
          {
            name: 'Settle',
            buttonProps: {
              variant: 'outlined',
            },
            action: () => {
              setDialogName('instruct');
              return Promise.resolve();
            },
          },
        ]);
      }
    }

    return actions;
  };

  return {
    onReject,
    createCollateralsActions,
    onSettle,
    onCancel,
    selectedContractId: selectedCid,
    onClose,
    dialogName,
  };
};
